import { COMMISSIONING_PHASE_KEYS } from "../enum/commissioning_phase_keys";
import { LIVE_DATA_WARNINGS } from "../enum/live_data_warnings";

export const COMMISSIONING_WIZARD_REDUCER = Object.freeze({
  FINAL_VERIFICATION_STEP_RECEIVED: "FINAL_VERIFICATION_STEP_RECEIVED",
  COMMISSIONING_DATA_RECEIVED: "COMMISSIONING_DATA_RECEIVED",
  COMMISSIONING_DEVICE_DATA_UPDATED: "COMMISSIONING_DEVICE_DATA_UPDATED",
  DEVICE_DATA_RECEIVED: "DEVICE_DATA_RECEIVED",
  SELECT_PHASE: "SELECT_PHASE",
  SELECT_FINAL_VERIFICATION: "SELECT_FINAL_VERIFICATION",
  SELECT_DEVICE_ID: "SELECT_DEVICE_ID",
  START_RESET: "START_RESET",
  STEP_WARNING_FOUND: "STEP_ERROR_FOUND",
  METERING_LIVE_DATA_FETCHED: "METERING_LIVE_DATA_FETCHED",
  METERING_DATA_FETCHED: "METERING_DATA_FETCHED",
});

export const commissioningWizardInitialState = {
  selectedDeviceId: null,
  selectedDeviceDbId: null,
  selectedFinalVerificationStepId: null,
  finalVerificationStep: null,

  selectedPhaseKey: COMMISSIONING_PHASE_KEYS.DEVICE_OVERVIEW, // default selected phase

  commissioningData: null,
  selectedPhase: null,

  selectedDevice: null,

  deviceCommissioningOverview: null,
  validationState: null,
  finalPhaseItems: null,

  // validation
  meteringDataQueryRan: false,
  meteringLiveDataQueryRan: false,
  stepValidationWarnings: [],
  warningOnStep: false,
};

function updateStateForFinalVerificationStepIdChange(state, selectedFinalVerificationStepId) {
  return {
    ...state,
    selectedFinalVerificationStepId: selectedFinalVerificationStepId,
    selectedDeviceId: null,
    selectedDeviceDbId: null,
    selectedDevice: null,
    meteringDataQueryRan: false,
    meteringLiveDataQueryRan: false,

    stepValidationWarnings: [],
    warningOnStep: false,
  };
}

function updateStateForDeviceIdChange(state, deviceId, deviceDbId) {
  return {
    ...state,
    selectedFinalVerificationStepId: null,
    finalVerificationStep: null,
    selectedDeviceId: deviceId,
    selectedDeviceDbId: deviceDbId,
    selectedDevice: null,
    meteringDataQueryRan: false,
    meteringLiveDataQueryRan: false,

    // Update the step validation state
    stepValidationWarnings: [],
    warningOnStep: false,
  };
}

function updateStateForPhaseChange(state, newPhase) {
  if (state.selectedPhaseKey === newPhase.phase_key) {
    return state;
  } else {
    return {
      ...state,
      selectedPhaseKey: newPhase.phase_key,
      selectedPhase: newPhase,
      selectedDeviceId: newPhase.devices?.[0]?.device_id || null,
      selectedDeviceDbId: newPhase.devices?.[0]?.device_db_id || null,
      selectedFinalVerificationStepId: newPhase.steps?.[0]?.step_id || null,
      finalVerificationStep: null,
      selectedDevice: null,

      // Update the step validation state
      stepValidationWarnings: [],
      warningOnStep: false,
      meteringDataQueryRan: false,
      meteringLiveDataQueryRan: false,
    };
  }
}

function sortDevices(payload) {
  if (Array.isArray(payload.phases)) {
    payload.phases.sort((a, b) => a.phase_key.localeCompare(b.phase_key));
    // Sort on Finished items first
    // then on device_name
    for (const phase of payload.phases) {
      if (phase.devices) {
        phase.devices.sort((a, b) => {
          // 64 is the indication that it was already commissioned
          const aPriority = a.required_action !== 64 ? 0 : 1;
          const bPriority = b.required_action !== 64 ? 0 : 1;

          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          return a.device_name.localeCompare(b.device_name);
        });
      }
      if (phase.steps) {
        phase.steps.sort((a, b) => {
          // 64 is the indication that it was already commissioned
          const aPriority = a.required_action !== 64 ? 0 : 1;
          const bPriority = b.required_action !== 64 ? 0 : 1;

          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          return a.date_created - b.date_created;
        });
      }
    }
  }
  return payload;
}

export const commissioningWizardReducer = (state, action) => {
  switch (action.type) {
    case COMMISSIONING_WIZARD_REDUCER.METERING_LIVE_DATA_FETCHED:
      if (!state.meteringLiveDataQueryRan) {
        return {
          ...state,
          meteringLiveDataQueryRan: action.payload,
        };
      }
      return state;
    case COMMISSIONING_WIZARD_REDUCER.METERING_DATA_FETCHED:
      if (!state.meteringDataQueryRan) {
        return {
          ...state,
          meteringDataQueryRan: action.payload,
        };
      }
      return state;
    case COMMISSIONING_WIZARD_REDUCER.FINAL_VERIFICATION_STEP_RECEIVED:
      if (action.payload.step_id === state.selectedFinalVerificationStepId) {
        return {
          ...state,
          finalVerificationStep: action.payload,
        };
      }
      return state;
    case COMMISSIONING_WIZARD_REDUCER.COMMISSIONING_DATA_RECEIVED:
      action.payload = sortDevices(action.payload);

      return {
        ...state,
        commissioningData: action.payload,
        deviceCommissioningOverview: action.payload?.device_commissioning_overview,
        validationState: action.payload?.validation_state,
        selectedPhase: action.payload?.phases?.find((phase) => phase.phase_key === state.selectedPhaseKey) || null,

        // Update the step validation state
        stepValidationWarnings: [],
        warningOnStep: false,
      };

    case COMMISSIONING_WIZARD_REDUCER.DEVICE_DATA_RECEIVED:
      if (action.payload.device_id === state.selectedDeviceId) {
        if (Array.isArray(action.payload)) {
          action.payload.sort((a, b) => a.step_key.localeCompare(b.step_key));
        }
        return {
          ...state,
          selectedDevice: action.payload,
        };
      }
      return state;
    case COMMISSIONING_WIZARD_REDUCER.STEP_WARNING_FOUND:
      if (action.payload === LIVE_DATA_WARNINGS.DEVICE_METERING_COMPONENT) {
        return {
          ...state,
          meteringDataQueryRan: true,
          warningOnStep: true,
          stepValidationWarnings: [...state.stepValidationWarnings, action.payload],
        };
      }
      if (action.payload === LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT) {
        return {
          ...state,
          meteringLiveDataQueryRan: true,
          warningOnStep: true,
          stepValidationWarnings: [...state.stepValidationWarnings, action.payload],
        };
      }
      return state;
    case COMMISSIONING_WIZARD_REDUCER.SELECT_PHASE:
      return updateStateForPhaseChange(state, action.payload);
    case COMMISSIONING_WIZARD_REDUCER.SELECT_DEVICE_ID:
      return state.selectedDeviceId !== action.payload.device_id
        ? updateStateForDeviceIdChange(state, action.payload.device_id, action.payload.device_db_id)
        : state;
    case COMMISSIONING_WIZARD_REDUCER.SELECT_FINAL_VERIFICATION:
      return state.selectedFinalVerificationStepId !== action.payload.finalVerificationStepId
        ? updateStateForFinalVerificationStepIdChange(state, action.payload.finalVerificationStepId)
        : state;
    case COMMISSIONING_WIZARD_REDUCER.START_RESET:
      return commissioningWizardInitialState;
    default:
      return state;
  }
};
